import { buildClasses } from "../utils/buildClasses";

/**
 * Open/close motion shared by modal layers (RyeModal and Settings).
 *
 * Opening, the panel grows from 96% while fading in, on a curve that starts
 * fast and settles slowly. Closing is quicker and accelerates away, shrinking
 * only slightly. With reduced motion turned on, panels only fade.
 *
 * A layer renders first in "entering", flips to "open" on the next frame so
 * the browser has painted the start state to transition from, and stays
 * mounted in "closing" for RYE_MODAL_EXIT_MS before unmounting.
 */
export type RyeModalPhase = "entering" | "open" | "closing";

/** Keep in sync with the duration-[140ms] classes below. */
export const RYE_MODAL_EXIT_MS = 140;

/**
 * The dimmed layer behind a modal's panel. Render it as the first child of the
 * modal's fixed, z-indexed container.
 *
 * It's always dimmed and fades in with opacity rather than background color:
 * browsers run opacity on the compositor, so the fade stays smooth while the
 * page is busy rendering the modal's content. A color fade runs on the main
 * thread and stutters when that's busy. It ignores clicks, so the container
 * still receives them (e.g. to close on a backdrop click).
 */
export function RyeModalBackdrop({
  phase,
  dimClass,
}: {
  phase: RyeModalPhase;
  /** The dimmed background, e.g. "bg-on-surface-900/10". */
  dimClass: string;
}): JSX.Element {
  return (
    <div
      aria-hidden="true"
      className={buildClasses(
        "absolute",
        "inset-0",
        "-z-10",
        "pointer-events-none",
        "transition-opacity",
        dimClass,
        phase === "closing"
          ? buildClasses("duration-[140ms]", "ease-in")
          : buildClasses("duration-150", "ease-out"),
        phase === "open" ? "opacity-100" : "opacity-0",
      )}
    />
  );
}

export function ryeModalPanelClasses(phase: RyeModalPhase): string {
  return buildClasses(
    "transition-[opacity,transform]",
    "motion-reduce:scale-100",
    phase === "entering"
      ? buildClasses("opacity-0", "scale-[0.96]")
      : phase === "open"
        ? buildClasses(
            "opacity-100",
            "scale-100",
            "duration-[220ms]",
            "ease-[cubic-bezier(0.16,1,0.3,1)]",
          )
        : buildClasses(
            "opacity-0",
            "scale-[0.98]",
            "duration-[140ms]",
            "ease-in",
          ),
  );
}
